import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  NotificationListAvatar,
  profileModeLabel,
  notificationLinkTarget,
  notificationDisplayTitle,
  notificationDisplayMessage,
} from './notificationUtils';
import { NotificationsTimelineSkeleton } from '../../layout/ReusableComponent/Skeleton/RouteSkeletons';
import '../../layout/ReusableComponent/Skeleton/RouteSkeletons.css';
import './AllNotifications.css';

const baseUrl = process.env.REACT_APP_BASE_URL || '';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

const groupByDate = (list) => {
  const groups = {};
  list.forEach((n) => {
    const d = new Date(n.notificationDate);
    const key = d.toDateString();
    if (!groups[key]) groups[key] = [];
    groups[key].push(n);
  });
  Object.keys(groups).forEach((k) => groups[k].sort((a, b) => new Date(b.notificationDate) - new Date(a.notificationDate)));
  const sortedKeys = Object.keys(groups).sort((a, b) => new Date(b) - new Date(a));
  return sortedKeys.map((key) => ({ dateLabel: key, items: groups[key] }));
};

const AllNotifications = ({ activeProfileMode = 'personal' }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [profileTab, setProfileTab] = useState(activeProfileMode === 'social' ? 'social' : 'personal');
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    setProfileTab(activeProfileMode === 'social' ? 'social' : 'personal');
  }, [activeProfileMode]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${baseUrl}/api/notifications/list`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profileMode: profileTab }),
      });
      const data = await res.json().catch(() => ({}));
      if (data?.success && Array.isArray(data.notifications)) setNotifications(data.notifications);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [profileTab]);

  useEffect(() => {
    document.title = 'Notifications';
    const params = new URLSearchParams(location.search);
    const sel = params.get('selected');
    setSelectedId(sel);
    fetchList();
  }, [fetchList, location.search]);

  const handleMarkRead = async (notificationId) => {
    try {
      await fetch(`${baseUrl}/api/notifications/mark-read`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notificationId }),
      });
      setNotifications((prev) =>
        prev.map((n) =>
          n.notificationId === notificationId ? { ...n, notificationStatus: 'read', userSeenDate: new Date().toISOString() } : n
        )
      );
    } catch (e) {
      // ignore
    }
  };

  const handleMarkAllRead = async () => {
    const unreadIds = notifications.filter((n) => n.notificationStatus === 'unread').map((n) => n.notificationId);
    if (unreadIds.length === 0) return;
    try {
      await fetch(`${baseUrl}/api/notifications/mark-read`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notificationIds: unreadIds }),
      });
      setNotifications((prev) =>
        prev.map((n) => (n.notificationStatus === 'unread' ? { ...n, notificationStatus: 'read', userSeenDate: new Date().toISOString() } : n))
      );
    } catch (e) {
      // ignore
    }
  };

  const handleItemClick = (n) => {
    if (n.notificationStatus === 'unread') handleMarkRead(n.notificationId);
    const target = notificationLinkTarget(n);
    if (target) navigate(target);
  };

  const filtered = notifications.filter((n) => {
    if (filter === 'unread') return n.notificationStatus === 'unread';
    if (filter === 'read') return n.notificationStatus === 'read';
    return true;
  });
  const grouped = groupByDate(filtered);
  const unreadCount = notifications.filter((n) => n.notificationStatus === 'unread').length;
  const hasUnread = unreadCount > 0;

  useEffect(() => {
    if (!selectedId || !notifications.length || loading) return;
    const el = document.getElementById(`notification-${selectedId}`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [selectedId, notifications, loading]);

  return (
    <div className={`all-notifications-page${hasUnread ? ' all-notifications-page--has-unread' : ''}`}>
      <div className="all-notifications-header">
        <div className="all-notifications-header-top">
          <h1 className="all-notifications-title">
            Notifications
            {hasUnread && <span className="all-notifications-title-badge" aria-label={`${unreadCount} unread`} />}
          </h1>
          <div className="all-notifications-profile-tabs" role="tablist" aria-label="Profile mode">
            {['personal', 'social'].map((mode) => (
              <button
                key={mode}
                type="button"
                role="tab"
                aria-selected={profileTab === mode}
                className={`all-notifications-profile-tab ${profileTab === mode ? 'active' : ''}`}
                onClick={() => setProfileTab(mode)}
              >
                {profileModeLabel(mode)}
              </button>
            ))}
          </div>
        </div>
        <div className="all-notifications-actions">
          <div className="all-notifications-filters">
            <button
              type="button"
              className={`all-notifications-filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button
              type="button"
              className={`all-notifications-filter-btn ${filter === 'unread' ? 'active' : ''}`}
              onClick={() => setFilter('unread')}
            >
              Unread {unreadCount > 0 && <span className="all-notifications-filter-count">({unreadCount})</span>}
            </button>
            <button
              type="button"
              className={`all-notifications-filter-btn ${filter === 'read' ? 'active' : ''}`}
              onClick={() => setFilter('read')}
            >
              Read
            </button>
          </div>
          {unreadCount > 0 && (
            <button type="button" className="all-notifications-mark-all-btn" onClick={handleMarkAllRead}>
              Mark all as read
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <NotificationsTimelineSkeleton rows={8} />
      ) : grouped.length === 0 ? (
        <p className="all-notifications-empty">No notifications to show.</p>
      ) : (
        <div className="all-notifications-timeline">
          {grouped.map(({ dateLabel, items }) => (
            <div key={dateLabel} className="all-notifications-timeline-group">
              <div className="all-notifications-timeline-date">
                {new Date(dateLabel).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
              <ul className="all-notifications-timeline-list">
                {items.map((n) => {
                  const title = notificationDisplayTitle(n);
                  const message = notificationDisplayMessage(n);
                  return (
                  <li
                    key={n.notificationId}
                    id={`notification-${n.notificationId}`}
                    className={`all-notifications-timeline-item ${n.notificationStatus === 'unread' ? 'unread' : ''} ${selectedId === n.notificationId ? 'selected' : ''}`}
                    onClick={() => handleItemClick(n)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') handleItemClick(n);
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <span className="all-notifications-timeline-dot" />
                    <span
                      className="all-notifications-timeline-icon"
                      title={n.postContentType ? `${n.postContentType} post` : n.kind || 'Notification'}
                    >
                      <NotificationListAvatar
                        notification={n}
                        size={20}
                        baseUrl={baseUrl}
                        profileMode={profileTab}
                      />
                    </span>
                    <div className="all-notifications-timeline-content">
                      <div className="all-notifications-timeline-content-top">
                        <strong className="all-notifications-timeline-title">{title}</strong>
                        {n.notificationStatus === 'unread' && (
                          <button
                            type="button"
                            className="all-notifications-timeline-mark-read"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkRead(n.notificationId);
                            }}
                          >
                            Mark as read
                          </button>
                        )}
                      </div>
                      {message ? <p className="all-notifications-timeline-message">{message}</p> : null}
                      <div className="all-notifications-timeline-meta">
                        <span>{formatDate(n.notificationDate)}</span>
                        {n.userSeenDate && <span className="all-notifications-timeline-seen">Seen: {formatDate(n.userSeenDate)}</span>}
                      </div>
                    </div>
                  </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AllNotifications;
